<template>
  <dynamic-object-form-structure
    :object-uuid="objectUUID"
    :object-layer="objectLayer"
    :loading-structure="loadingStructure"
    :loading-save="loadingSave"
    :permission-name="form.permission"
    :object-type-translation-key="form.name_translation"
    :tabs="visibleTabs"
    :saveIsDisabled="!required.filled"
    :active-tab="activeTab"
    :action-type="actionType"
    :map-element="$parent.$refs.formsContainer"
    :previous-pos-x="previousPosX"
    :previous-pos-y="previousPosY"
    :data-was-changed="dataWasChanged"
    :form-height-update="formSizeUpdate"
    :fields-still-need-filled="required.stillNeedFilled"
    :min-width="765"
    @updateActiveTab="activeTab = $event"
    @changeViewMode="changeViewModeOfObject"
    @openDetails="openDetails"
    @refresh="refresh"
    @clearData="clearData"
    @save="saveObject"
    @close="closeForm"
    @resize-form="formSizeUpdate++"
    :object-data="objectData"
  >
    <template v-slot:formFields>
      <closures-form-splitters-tab
        v-show="
          visibleTabs[activeTab].code ===
            splitterSplicingTabCodePrefix + 'splitters'
        "
        :network-level="objectData.tl_network_level"
        object-endpoint="technicallocals"
        :object-uuid="objectData.tl_uuid"
        :object-id="objectData.tl_id"
        :is-tech-local="true"
        :disabled="
          readOnly ||
            actionType === 'create' ||
            !userCanEditTabContent(visibleTabs[activeTab])
        "
        :action-type="actionType"
      />

      <closure-form-splicing-tab
        v-show="
          visibleTabs[activeTab].code ===
            splitterSplicingTabCodePrefix + 'splicing'
        "
        object-endpoint="technicallocals"
        :object-uuid="objectData.tl_uuid"
        :object-id="objectData.tl_id"
        :network-level="objectData.tl_network_level"
        :visible="
          visibleTabs[activeTab].code ===
            splitterSplicingTabCodePrefix + 'splicing'
        "
        :form-size-update="formSizeUpdate"
        :is-tech-local="true"
        :disabled="
          readOnly || actionType === 'create' || visibleTabs[activeTab].disable
        "
        :action-type="actionType"
        @update-connections="updatedSplicingConnections = $event"
        @set-initial-connections="initialConnections = $event"
        @update-form-height="formSizeUpdate++"
      />

      <div
        v-show="visibleTabs[activeTab].type === 'form'"
        class="form tab-content"
      >
        <template v-for="field in visibleTabs[activeTab].fields">
          <input-field
            v-if="field.var_name === 'tl_virtual_connection_id'"
            :key="visibleTabs[activeTab].code + field.label + field.order"
            ref="formInputField"
            :value="getValueInObject(field.var_name, field.input_type)"
            :label="field.label_client ? field.label_client : field.label"
            :variable-name="field.var_name"
            inputType="other"
            :size="field.size"
            :required="field.required"
            :required-filled="required.filled"
            :hide="field.hide"
            :fieldDisabled="field.disable"
            :tabDisabled="!userCanEditTabContent(visibleTabs[activeTab])"
            :formDisabled="readOnly"
          >
            <div>
              <treeselect
                :value="getValueInObject(field.var_name, field.input_type)"
                @input="
                  setValueInObject(field.var_name, $event, field.input_type)
                "
                :options="virtualConnectionOptions"
                :load-options="loadVirtualConnectionOptions"
                :disabled="
                  readOnly ||
                    !userCanEditTabContent(visibleTabs[activeTab]) ||
                    field.disable
                "
                class="form-input-select"
                disableFuzzyMatching
                disableBranchNodes
              />
            </div>
          </input-field>
          <input-field
            v-else-if="field.var_name === 'tl_material_id'"
            :key="visibleTabs[activeTab].code + field.label + field.order"
            ref="formInputField"
            :value="getValueInObject(field.var_name, field.input_type)"
            :label="field.label_client ? field.label_client : field.label"
            :variable-name="field.var_name"
            inputType="other"
            :size="field.size"
            :required="field.required"
            :required-filled="required.filled"
            :hide="field.hide"
            :fieldDisabled="
              field.disable || objectData.tl_network_level === null
            "
            :tabDisabled="!userCanEditTabContent(visibleTabs[activeTab])"
            :formDisabled="readOnly"
          >
            <div>
              <treeselect
                :value="getValueInObject(field.var_name, field.input_type)"
                @input="
                  setValueInObject(field.var_name, $event, field.input_type)
                "
                :options="materialOptions"
                :load-options="loadMaterialOptions"
                :normalizer="materialOptionsNormalizer"
                :disabled="
                  readOnly ||
                    !userCanEditTabContent(visibleTabs[activeTab]) ||
                    field.disable ||
                    objectData.tl_network_level === null
                "
                class="form-input-select"
                disableFuzzyMatching
              />
            </div>
          </input-field>

          <input-field
            v-else
            :key="visibleTabs[activeTab].code + field.label + field.order"
            ref="formInputField"
            :value="getValueInObject(field.var_name, field.input_type)"
            @input="setValueInObject(field.var_name, $event, field.input_type)"
            :label="field.label_client ? field.label_client : field.label"
            :variable-name="field.var_name"
            :inputType="field.input_type"
            :size="field.size"
            :required="field.required"
            :required-filled="required.filled"
            :selectOptions="field.select_list_name"
            :radioOptions="field.radio_options"
            :fieldDisabled="field.disable"
            :tabDisabled="!userCanEditTabContent(visibleTabs[activeTab])"
            :formDisabled="readOnly"
            :hide="field.hide"
            :checkboxOptions="field.checkbox_options"
            :object-data="objectData"
            :labelIsLocked="objectData.tl_label_locked"
            :user-can-unlock-label="!userHasFibercloudPermission(form.permission + '_manage_label')"
            @toggle-label-lock="changeLabelMode"
            @updateCheckboxValue="updateCheckboxValue($event)"
            @removePhoto="addToPhotosToRemove($event)"
          />
        </template>
      </div>
      <custom-fields-tab
        v-if="visibleTabs[activeTab].code === 'custom_fields'"
        :customFields="customFields.custom_fields"
        :objectData="objectData.custom_fields"
        :actionType="actionType"
        :disabled="readOnly"
        @updateValue="setValueInObject"
        :requiredFilled="required.filled"
      />
    </template>
  </dynamic-object-form-structure>
</template>

<script>
import { dynamicObjectFormsMixin } from "@/mixins/forms/dynamicObjectForms/dynamicObjectFormsMixin";
import DynamicObjectFormStructure from "../_Templates/DynamicObjectFormStructure";
import InputField from "../_Molecules/InputField";
import Treeselect from "@riophae/vue-treeselect";
import ClosuresFormSplittersTab from "@/components/Home/Forms/_SpecialTabs/Splitters/SplittersTab";
import ClosureFormSplicingTab from "@/components/Home/Forms/_SpecialTabs/Splicing/SplicingTab";
import CustomFieldsTab from "@/components/Home/Forms/_SpecialTabs/CustomFields/CustomFieldsTab.vue";
import { checkPermissionsMixin } from "@/mixins/checkPermissionsMixin";

export default {
  name: "TechnicalLocalForm",

  components: {
    ClosureFormSplicingTab,
    ClosuresFormSplittersTab,
    InputField,
    DynamicObjectFormStructure,
    Treeselect,
    CustomFieldsTab
  },

  mixins: [dynamicObjectFormsMixin,checkPermissionsMixin],

  props: {
    /**
     *Technical point or technical site to serve as base for closures or technical locals, respectively.
     */
    technicalSiteData: {
      type: Object,
      required: false
    }
  },

  data() {
    return {
      formSizeUpdate: 0,

      /**
       * List of virtual connection options
       */
      virtualConnectionOptions: null
    };
  },

  computed: {
    materialRequestQueryParameters() {
      let queryParameters = {
        workspace_uuid: this.$store.getters.workspace.uuid,
        material_family_id: "cl"
      };
      if (this.objectData.tl_network_level) {
        queryParameters.network_level_id = this.objectData.tl_network_level;
      }
      return queryParameters;
    },

    splitterSplicingTabCodePrefix() {
      let codePrefix = this.objectLayer.replace("fc_", "");
      return codePrefix + "_";
    }
  },

  watch: {
    loadingStructure(newValue) {
      if (this.actionType === "splicing") {
        let splicingTabIndex = this.visibleTabs.findIndex(tab =>
          tab.code.includes("splicing")
        );
        if (splicingTabIndex > -1) {
          this.activeTab = splicingTabIndex;
        }
      }

      if (!newValue) {
        this.objectData.coordinate_x = this.xCoordinate;
        this.objectData.coordinate_y = this.yCoordinate;
        this.getPreviousImages();
      }
    },
    "objectData.tl_network_level"() {
      this.materialOptions = null;
    }
  },

  mounted() {
    this.loadVirtualConnectionOptions();
  },

  methods: {
    createObject() {
      this.dataToSend = {
        technical_site: { uuid: this.technicalSiteData.ts_uuid },
        status: this.objectData.tl_status
      };
      this.placeObjectDataInDataToSend();

      this.CREATE_OBJECTS({
        endpoint: this.form.endpoint,
        data: [this.dataToSend]
      })
        .then(() => {
          this.finishObjectCreation();
        })
        .catch(error => {
          this.$toasted.global.toast_error(error.response.data.data);
          this.loadingSave = false;
        });
    },

    /**
     * Get all technical locals grouped by logical type
     * @returns {Promise<void>}
     */
    async loadVirtualConnectionOptions() {
      try {
        let allOptions = await this.getAllObject({
          endpoint: "technicallocals",
          fields: "tl_id,tl_label,tl_technical_site_id"
        });

        let optionsGroupByLogicalType = this.groupOptionsByLogicalType(
          allOptions
        );

        let finalList = [];
        for (const [query, value] of Object.entries(
          optionsGroupByLogicalType
        )) {
          if (query === "FDC" || query === "OLT") {
            let children = [];

            value.forEach(option => {
              children.push(this.virtualConnectionOptionsNormalizer(option));
            });

            finalList.push({
              id: query,
              label: query,
              children: children
            });
          }
        }
        this.virtualConnectionOptions = finalList;
      } catch (e) {
        console.error(e);
      }
    },

    /**
     * Returns the list grouped by logical type
     * @param allOptions
     * @returns {*}
     */
    groupOptionsByLogicalType(allOptions) {
      return allOptions.reduce((r, a) => {
        r[a.tl_technical_site_logical_type] = [
          ...(r[a.tl_technical_site_logical_type] || []),
          a
        ];
        return r;
      }, {});
    },

    /**
     * Normalizes the key-value pairs of each given list option.
     * @param node - option being normalized.
     * @returns {{id: *, label: *}}
     */
    virtualConnectionOptionsNormalizer(node) {
      return {
        id: node.tl_id,
        label: "ID:" + node.tl_id + "- " + node.tl_label
      };
    }
  }
};
</script>



// WEBPACK FOOTER //
// src/components/Home/Forms/ObjectForms/TechnicalLocalForm.vue